import { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Dropdown, Form, FormLabel } from "react-bootstrap";
import OverflowLabel from "./OverflowLabel";
import { onlyUnique } from "helpers/Functions";
import { ZCounter } from "./ZCounter";
import { ZCompactSearchBar } from "./ZCompactSearchBar";
import { ZValidation } from "models/common/validation";
import { ZIconType } from "helpers/utility/iconMap";
import { ZIcon } from "./ZIcon";
import { ZTooltip } from "./ZTooltip";
import { v4 as uuidv4 } from 'uuid';
import { ZLabel } from "./ZLabel";

type ZMultiSelectDropdownProps<T> = {
    options: T[];
    values?: T[];
    id?: string;
    label?: React.ReactNode;
    hasAllOption?: boolean;
    placeholder?: string;
    hasSearch?: boolean;
    searchPlaceholder?: string;
    canClear?: boolean;
    dropdownIsDisabled?: boolean;
    barSize?: "compact" | "normal" | "fill";
    validation?: ZValidation;
    maxShownSelectedOptions?: number; // The maximum number of options that should show their full name before resorting to "X selected"
    containerClassName?: string;
    containerStyle?: React.CSSProperties;
    minMenuHeightPx?: number;
    maxMenuHeightPx?: number;
    minMenuWidthPx?: number;
    maxMenuWidthPx?: number;
    value: (option: T) => any;
    display: (option: T) => string;
    disabled: (option: T) => boolean;
    onSelectionChange: (newOptions: T[]) => void;
    onAddNewOption?: (value: string) => Promise<void>;
    onDropdownClose?: () => void;
    itemClassName?: (option: T) => string;
    hideCheckbox?: (option: T) => boolean;
}

export function ZMultiSelectDropdown<T,>({options, values, id, label, hasAllOption, placeholder, hasSearch, searchPlaceholder, canClear, dropdownIsDisabled, barSize, validation, maxShownSelectedOptions = 3, containerClassName, containerStyle, minMenuHeightPx, maxMenuHeightPx, minMenuWidthPx, maxMenuWidthPx, value, display, disabled, onSelectionChange, onAddNewOption, onDropdownClose, itemClassName, hideCheckbox }: ZMultiSelectDropdownProps<T>) {
    const [enabledOptions, setEnabledOptions] = useState<any[]>([]);
    const [hovering, setHovering] = useState(false);
    const [searchText, setSearchText] = useState<string>("");
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [shownOptions, setShownOptions] = useState<T[]>([]);
    const dropdownId = id ?? uuidv4();

    const closeDropdown = () => {
        setIsDropdownOpen(false);
    }

    const openDropdown = () => {
        setIsDropdownOpen(true);
    }

    useEffect(() => {
        if (isDropdownOpen) {
            // Focus search bar when dropdown is opened
            document.getElementById(`${dropdownId}-search-bar`)?.focus({preventScroll: true});
        } else {
            setSearchText("");
        }
    }, [isDropdownOpen]);

    useEffect(() => {
        setEnabledOptions([...options].filter(opt => !disabled(opt)));
    }, [options, disabled]);

    useEffect(() => {
        setShownOptions(filterBySearch(options, hasSearch ? searchText : ""));
    }, [options, hasSearch, searchText]);

    function valuesToLabelText(values?: any[]): string {
        if (values != null && values.length > 0) {
            if (values.length === options.length) {
                return `All ${values.length} selected`;
            } else {
                if (values.length <= maxShownSelectedOptions) {
                    return values.map(opt => display(opt)).join(", ")
                } else {
                    return values.length + " selected";
                }
            }
        } else {
            return placeholder != null ? placeholder : "Select options";
        }
    }

    function filterBySearch(options: any[], search: string): any[] {
        return options.filter(opt => display(opt).containsKeyword(search));
    }

    function hasExactMatch(options: any[], search: string): boolean { 
        return options.findIndex(o => display(o).toLocaleLowerCase().match(new RegExp(`^${search.trim().toLocaleLowerCase()}$`, 'g')) != null) != -1;
    }

    return (
        <div className={containerClassName} style={{ ...containerStyle }}>
        {(label ? <ZLabel preventSelect>{label}</ZLabel> : <></>)}
        <Dropdown onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
                closeDropdown();
                onDropdownClose && onDropdownClose();
            }
        }} show={isDropdownOpen} className='d-flex align-items-center multi-select-dropdown w-100' autoClose={'outside'}>
            <Dropdown.Toggle 
                onClick={(e) => {
                    isDropdownOpen && onDropdownClose && onDropdownClose();
                    setIsDropdownOpen(!isDropdownOpen);
                    e.stopPropagation();
                    e.preventDefault();
                }} 
                disabled={dropdownIsDisabled} 
                style={{ overflow: "hidden" }}
                id="dropdown-basic" 
                variant='light' 
                className={`d-flex align-items-center rounded select-dropdown-toggle ${barSize && `select-dropdown-toggle-${barSize} ${validation?.invalid && `invalid`}`}`} 
                onMouseEnter={() => setHovering(true)} 
                onMouseLeave={() => setHovering(false)}
            >
                <OverflowLabel isVisible={true} labelClassName={`ps-1 select-dropdown-toggle-text ${(values == null || values.length === 0) && "select-dropdown-toggle-text-placeholder"}`} tooltipStyle={{flexGrow: 1}} text={valuesToLabelText(values)} />
                {values != null && canClear && values.length > 0 && hovering ? <span className="mdi mdi-close ms-auto select-dropdown-clear-button" onClick={(e) => {
                    e.stopPropagation();
                    onSelectionChange([]);
                }} /> : <></>}
                {validation?.invalid && <ZTooltip enabled text={validation?.error}><ZIcon variant="danger" icon={ZIconType.Error} /></ZTooltip>}
            </Dropdown.Toggle>

            <Dropdown.Menu 
                tabIndex={0} 
                style={{
                    overflow: "hidden", 
                    minHeight: minMenuHeightPx != null ? `${minMenuHeightPx}px` : undefined, 
                    maxHeight: `${maxMenuHeightPx ?? 300}px`, 
                    minWidth: minMenuWidthPx != null ? `${minMenuWidthPx}px` : undefined,
                    maxWidth: maxMenuHeightPx != null ? `${maxMenuWidthPx}px` : undefined,
                }} className="select-dropdown-menu py-0" onKeyDown={(e) => {
                if (e.key === "Escape") {
                    closeDropdown();
                    e.stopPropagation();
                    e.preventDefault();
                }
            }}>
                <div className="d-flex flex-column" style={{ maxHeight: "inherit" }}> {/** This div is just to wrap the content so that we can get correct overflow on the list of items */}
                { hasSearch ? <div className="bg-white dropdown-search-bar"><ZCompactSearchBar id={`${dropdownId}-search-bar`} className="mb-0" placeholderText={searchPlaceholder} searchText={searchText} onChange={(t) => setSearchText(t)} /></div> : <></> }
                { onAddNewOption != null && searchText.trim().length > 0 && !hasExactMatch(options, searchText) ? 
                    <span
                        className={`d-flex dropdown-item ps-2 align-items-center select-dropdown-item mt-1`}
                        key={"add-new-opt"}
                        tabIndex={0}
                        id={dropdownId + "-add-new-opt"}
                        draggable={false}
                        onClick={(x) => {
                            onAddNewOption(searchText.trim());
                            closeDropdown();
                            x.stopPropagation();
                            x.preventDefault();
                        }}
                        onKeyDown={(x) => {
                            if (x.key === "Enter" || x.key === " ") {
                                onAddNewOption(searchText.trim());
                                closeDropdown();
                                x.stopPropagation();
                                x.preventDefault();
                            }
                        }}
                >
                    <ZIcon icon={ZIconType.Add} iconClassName="me-1"/>
                    <OverflowLabel labelClassName="prevent-select" isVisible={isDropdownOpen} text={`Add \"${searchText.trim()}\"`} /></span> :
                    <></>
                }
                {hasSearch && <hr className="m-1 border-1" />}
                <div style={{overflowY: "auto"}}>
                { hasAllOption === true ? 
                    <><span
                        tabIndex={0}
                        key={-1}
                        id={"-1"}
                        className="d-flex dropdown-item ps-2 align-items-center select-dropdown-item prevent-select"
                        draggable={false}
                        onClick={(x) => {
                            if (values && values.length >= enabledOptions.length) {
                                // Remove selection from list
                                onSelectionChange([]);
                            } else {
                                // Add selection to list
                                const newOptions = enabledOptions;
                                onSelectionChange(newOptions);
                            }
                        }}
                        onKeyDown={(x) => {
                            if (x.key === "Enter" || x.key === " ") {
                                if (values && values.length >= enabledOptions.length) {
                                    // Remove selection from list
                                    onSelectionChange([]);
                                } else {
                                    // Add selection to list
                                    const newOptions = enabledOptions;
                                    onSelectionChange(newOptions);
                                }
                                x.stopPropagation();
                                x.preventDefault();
                            }
                        }}
                    >
                        <Form.Check
                            type="checkbox"
                            className="vertical-align-middle pe-2"
                            id="default-checkbox"
                            tabIndex={-1}
                            checked={values && values.length >= enabledOptions.length}
                            onChange={() => {}}
                        />All <ZCounter className="ms-1" counter={enabledOptions.length}></ZCounter></span> <hr className="mt-1 mb-1 border-1 mx-1" /></>: <></>}
                {shownOptions.length === 0 ? 
                    <span className="d-flex px-2 py-1 align-items-center prevent-select">Nothing here...</span> 
                : shownOptions.map((e: any, i: number) => (
                    <span
                        className={`d-flex dropdown-item prevent-select ps-2 align-items-center select-dropdown-item ${disabled(e) && "disabled"}`}
                        key={i}
                        tabIndex={disabled(e) ? -1 : 0}
                        id={value(e).toString()}
                        draggable={false}
                        onClick={(x) => {
                            if (values?.some((opt: T) => (value(opt) == value(e)))) {
                                // Remove selection from list
                                const newSelectedOptions = [...values].filter(opt => value(opt) !== value(e));
                                onSelectionChange(newSelectedOptions);
                            } else {
                                // Add selection to list, sort the array so that we're adding e into the right spot in the array (to match the order of the options)
                                const newSelectedOptions = [...values ?? [], e].filter(onlyUnique).slice().sort((a, b) => options.indexOf(a) - options.indexOf(b));
                                onSelectionChange(newSelectedOptions);
                            }
                        }}
                        onKeyDown={(x) => {
                            if (x.key === "Enter" || x.key === " ") {
                                if (values?.some((opt: T) => (value(opt) == value(e)))) {
                                    // Remove selection from list
                                    const newSelectedOptions = [...values].filter(opt => value(opt) !== value(e));
                                    onSelectionChange(newSelectedOptions);
                                } else {
                                    // Add selection to list, sort the array so that we're adding e into the right spot in the array (to match the order of the options)
                                    const newSelectedOptions = [...values ?? [], e].filter(onlyUnique).slice().sort((a, b) => options.indexOf(a) - options.indexOf(b));
                                    onSelectionChange(newSelectedOptions);
                                }
                                x.stopPropagation();
                                x.preventDefault();
                            }
                        }}
                    >
                        {hideCheckbox && hideCheckbox(e) ? (
                            <OverflowLabel isVisible={isDropdownOpen} text={display(e)} />
                        ) :
                        (
                            <>
                                <Form.Check
                                    tabIndex={-1}
                                    type="checkbox"
                                    className={`vertical-align-middle pe-2 ${itemClassName && itemClassName(e)}`}
                                    id={dropdownId + "-" + value(e)}
                                    key={dropdownId + "-" + value(e)}
                                    value={value(e).toString()}
                                    disabled={disabled(e)}
                                    checked={values?.some((opt: T) => (value(opt) == value(e)))}
                                    onChange={() => {}}
                                /><OverflowLabel isVisible={isDropdownOpen} text={display(e)} />
                            </>
                        )} 
                    </span>

                ))}</div>
                </div>
            </Dropdown.Menu>
        </Dropdown></div>
    );
};